import { Box, CircularProgress, IconButton, styled, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

export type MultiSnapshotMap = Record<string, string | null>;

const TILE_WIDTH = 80;
const TILE_HEIGHT = 52;
const TILE_GAP = 8;
const MAX_VISIBLE_TILES = 9;
const MAX_SCROLL_WIDTH = TILE_WIDTH * MAX_VISIBLE_TILES + TILE_GAP * (MAX_VISIBLE_TILES - 1);

const TileContainer = styled(Box)<{
    selected: boolean;
    isNull: boolean;
    isAvailableButNotLoaded: boolean;
}>(({ selected, isNull, isAvailableButNotLoaded }) => {
    let backgroundColor = "#e8e8e8";
    if (isNull) backgroundColor = "#d5d5d5";
    else if (isAvailableButNotLoaded) backgroundColor = "#F3EDFB";

    return {
        position: "relative",
        width: TILE_WIDTH,
        height: TILE_HEIGHT,
        minWidth: TILE_WIDTH,
        borderRadius: 4,
        overflow: "hidden",
        cursor: "pointer",
        border: selected ? "2px solid #6B439D" : "2px solid transparent",
        backgroundColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "border-color 0.15s ease",
        "&:hover": {
            borderColor: selected ? "#6B439D" : "#aaa",
        },
    };
});

const KeyLabel = styled(Typography)({
    position: "absolute",
    top: 4,
    left: 4,
    fontSize: "9px",
    fontWeight: 500,
    letterSpacing: 1,
    color: "#fff",
    backgroundColor: "rgba(107,67,157,0.88)",
    padding: "2px 6px",
    borderRadius: 999,
    zIndex: 1,
    lineHeight: 1,
});

const TileImage = styled("img")({
    width: "100%",
    height: "100%",
    objectFit: "cover",
});

const ArrowButton = styled(IconButton)({
    padding: 2,
    width: 24,
    height: 24,
    flexShrink: 0,
    color: "#6B439D",
    "&.Mui-disabled": {
        color: "#ccc",
    },
});

interface SnapshotCarouselProps {
    snapshotMap: MultiSnapshotMap;
    selectedKey: string;
    loadedImages: Record<string, string>;
    loadingKey: string | null;
    onTileClick: (key: string) => void;
}

function SnapshotCarousel({
    snapshotMap,
    selectedKey,
    loadedImages,
    loadingKey,
    onTileClick,
}: SnapshotCarouselProps): JSX.Element {
    const sortedKeys = useMemo(
        () => Object.keys(snapshotMap).sort((a, b) => Number(a) - Number(b)),
        [snapshotMap],
    );
    const snapshotKeysSignature = sortedKeys.join("|");

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const tileRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const userNavigatedCarouselRef = useRef(false);
    const selectedKeyRef = useRef(selectedKey);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    useEffect(() => {
        selectedKeyRef.current = selectedKey;
    }, [selectedKey]);

    useEffect(() => {
        userNavigatedCarouselRef.current = false;
    }, [snapshotKeysSignature]);

    const updateScrollButtons = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    const centerTileInViewport = useCallback((key: string, behavior: "auto" | "smooth" = "smooth") => {
        const container = scrollRef.current;
        const tile = tileRefs.current[key];
        if (!container || !tile) return;

        const containerRect = container.getBoundingClientRect();
        const tileRect = tile.getBoundingClientRect();

        const delta =
            (tileRect.left - containerRect.left) - (containerRect.width - tileRect.width) / 2;

        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        const targetScrollLeft = container.scrollLeft + delta;
        const clampedLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

        if (behavior === "smooth") {
            container.scrollTo({ left: clampedLeft, behavior: "smooth" });
        } else {
            container.scrollLeft = clampedLeft;
        }
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;

        updateScrollButtons();

        const observer = new ResizeObserver(() => {
            updateScrollButtons();
            if (selectedKeyRef.current === "0" && !userNavigatedCarouselRef.current) {
                centerTileInViewport("0", "auto");
            }
        });

        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [updateScrollButtons, centerTileInViewport]);

    useEffect(() => {
        if (selectedKey !== "0" || userNavigatedCarouselRef.current) return undefined;
        let raf = 0;
        let attempts = 0;

        const tryCenter = () => {
            attempts += 1;
            const container = scrollRef.current;
            const tile = tileRefs.current["0"];

            if (container && tile && container.clientWidth > 0) {
                centerTileInViewport("0", "auto");
                updateScrollButtons();
                return;
            }

            if (attempts < 8) {
                raf = requestAnimationFrame(tryCenter);
            }
        };

        raf = requestAnimationFrame(tryCenter);

        return () => {
            if (raf) cancelAnimationFrame(raf);
        };
    }, [selectedKey, snapshotKeysSignature, centerTileInViewport, updateScrollButtons]);

    function scrollBy(direction: "left" | "right") {
        userNavigatedCarouselRef.current = true;
        const el = scrollRef.current;
        if (!el) return;
        const amount = (TILE_WIDTH + TILE_GAP) * 2;
        el.scrollBy({
            left: direction === "left" ? -amount : amount,
            behavior: "smooth",
        });
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mt={1}
            sx={{
                width: "100%",
                height: TILE_HEIGHT,
                minHeight: TILE_HEIGHT,
                maxHeight: TILE_HEIGHT,
                overflow: "hidden",
            }}
        >
            <ArrowButton onClick={() => scrollBy("left")} disabled={!canScrollLeft} size="small">
                <MdChevronLeft size={22} />
            </ArrowButton>
            <Box
                ref={scrollRef}
                onScroll={updateScrollButtons}
                display="flex"
                gap={`${TILE_GAP}px`}
                sx={{
                    overflowX: "hidden",
                    overflowY: "hidden",
                    flex: 1,
                    maxWidth: MAX_SCROLL_WIDTH,
                    height: TILE_HEIGHT,
                    scrollSnapType: "none",
                    justifyContent: canScrollLeft || canScrollRight ? "flex-start" : "center",
                }}
            >
                {sortedKeys.map((key) => {
                    const url = snapshotMap[key];
                    const isNull = url === null;
                    const isAvailable = !isNull;
                    const isSelected = key === selectedKey;
                    const loadedSrc = loadedImages[key];
                    const isLoading = loadingKey === key;
                    const isAvailableButNotLoaded = isAvailable && !loadedSrc && !isLoading;

                    let content: JSX.Element;
                    if (isLoading) {
                        content = <CircularProgress size={20} />;
                    } else if (loadedSrc) {
                        content = <TileImage src={loadedSrc} alt={`Snapshot ${key}s`} />;
                    } else {
                        content = (
                            <Typography fontSize="10px" fontWeight={500} color={isNull ? "#888" : "#6B439D"}>
                                {isNull ? "N/A" : "IMG"}
                            </Typography>
                        );
                    }

                    return (
                        <TileContainer
                            key={key}
                            ref={(node) => {
                                tileRefs.current[key] = node as HTMLDivElement | null;
                            }}
                            selected={isSelected}
                            isNull={isNull}
                            isAvailableButNotLoaded={isAvailableButNotLoaded}
                            onClick={() => {
                                userNavigatedCarouselRef.current = true;
                                onTileClick(key);
                            }}
                            sx={{ scrollSnapAlign: "start" }}
                        >
                            <KeyLabel>{`${key}s`}</KeyLabel>
                            {content}
                        </TileContainer>
                    );
                })}
            </Box>
            <ArrowButton onClick={() => scrollBy("right")} disabled={!canScrollRight} size="small">
                <MdChevronRight size={22} />
            </ArrowButton>
        </Box>
    );
}

export default SnapshotCarousel;
